/**
 * GUID Validation Utilities
 * 
 * This module provides utilities for validating and normalizing GUID identifiers
 * used throughout the application after the int-to-GUID migration (spec 008/009).
 * 
 * All entity identifiers (Client, User, Lead, Property, Subscription, etc.) must
 * be valid RFC 4122 UUID v4 GUIDs in string format.
 * 
 * @module guidValidation
 */

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validates if a value is a properly formatted GUID string.
 * 
 * @param {any} value - The value to validate (will be converted to string)
 * @returns {boolean} True if value is a valid, non-null GUID; false otherwise
 * 
 * @example
 * isValidGuid("66f2cb93-d3fb-4cba-9562-558192a08556") // true
 * isValidGuid("00000000-0000-0000-0000-000000000000") // false (null GUID)
 * isValidGuid(66) // false (integer ID)
 * isValidGuid("") // false
 * isValidGuid(null) // false
 */
export function isValidGuid(value) {
  if (value == null || value === "") return false;
  const text = String(value).trim();
  if (!GUID_PATTERN.test(text)) return false;
  return text !== "00000000-0000-0000-0000-000000000000";
}

/**
 * Normalizes an entity ID to lowercase GUID format.
 * Returns null if the value is not a valid GUID.
 * 
 * @param {any} value - The entity ID to normalize
 * @returns {string|null} Lowercase GUID string or null if invalid
 * 
 * @example
 * normalizeEntityId("66F2CB93-D3FB-4CBA-9562-558192A08556") // "66f2cb93-d3fb-4cba-9562-558192a08556"
 * normalizeEntityId(66) // null
 * normalizeEntityId("invalid") // null
 */
export function normalizeEntityId(value) {
  if (value == null || value === "") return null;
  const text = String(value).trim();
  if (!isValidGuid(text)) return null;
  return text.toLowerCase();
}

/**
 * Detects if a value looks like a legacy integer ID from pre-migration data.
 * Used to identify stale cached data or legacy URLs that need migration.
 * 
 * @param {any} value - The value to check
 * @returns {boolean} True if value is a positive integer format
 * 
 * @example
 * looksLikeLegacyInteger(66) // true
 * looksLikeLegacyInteger("123") // true
 * looksLikeLegacyInteger("66f2cb93-d3fb-4cba-9562-558192a08556") // false
 * looksLikeLegacyInteger(0) // false
 * looksLikeLegacyInteger(-5) // false
 */
export function looksLikeLegacyInteger(value) {
  if (value == null || value === "") return false;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return false;
  const parsed = Number.parseInt(text, 10);
  return Number.isInteger(parsed) && parsed > 0;
}

/**
 * Formats a user-friendly error message for GUID validation failures.
 * 
 * @param {string} fieldName - The name of the field that failed validation
 * @param {any} value - The invalid value
 * @returns {string} Formatted error message
 * 
 * @example
 * formatGuidError("clientId", 66) // "Invalid clientId format. Expected GUID, got: 66"
 */
export function formatGuidError(fieldName, value) {
  return `Invalid ${fieldName} format. Expected GUID, got: ${JSON.stringify(value)}`;
}
